// English source strings of Routing Intelligence v2 (decision D24, PRD §33): the routing decision of a task, the model
// performance tab, the prompt cache dashboard and the Jev classifier card. Merged into `en`; pt-BR lives in
// pt-BR-routing.ts (complete, checked by the type).
export const enRouting = {
  "ri.unknown": "unknown",

  // Model tiers in the routing configuration editor (D29)
  "ri.tiers.title": "Routing Intelligence — model tiers",
  "ri.tiers.hint": "Which models new builder attempts may use per tier. Saved in the draft; nothing changes until the version is activated.",
  "ri.tiers.enabled": "Routing Intelligence enabled",
  "ri.tiers.enabledHint": "Off by default. When on, new builder attempts are routed through the economy and premium tiers below.",
  "ri.tiers.economy": "Economy models",
  "ri.tiers.economyHint": "Tried first, in this order. Free models are allowed.",
  "ri.tiers.premium": "Premium models",
  "ri.tiers.premiumHint": "Used when a task needs more. Empty means the Maestro model and its substitutes.",
  "ri.tiers.count": "{n} of {max} selected",
  "ri.tiers.full": "Maximum of {max} reached: remove one to add another.",
  "ri.tiers.noTargets": "No chat model registered yet. Add one in Settings → Models & combos.",
  "ri.tiers.via": "via {connection}",
  "ri.tiers.notVerified": "({state})",
  "ri.tiers.unknownTarget": "(unknown model)",
  "ri.tiers.unverifiedWarning": "Routing skips models that are not verified until they are verified.",

  // Vocabulary
  "ri.tier.deterministic": "Deterministic",
  "ri.tier.economy": "Economy",
  "ri.tier.premium": "Premium",
  "ri.source.rules": "Rules",
  "ri.source.jev": "Jev",
  "ri.source.jev_rules": "Jev combined with rules",
  "ri.source.conservative": "Low confidence, conservative",
  "ri.source.degraded": "Degraded — rules",
  "ri.cache.unknown": "Cache state unknown",
  "ri.cache.cold": "Cold cache",
  "ri.cache.warming": "Warming cache",
  "ri.cache.warm": "Warm cache",
  "ri.cache.lost": "Cache lost",
  "ri.affinity.openrouter_session_id": "OpenRouter session",
  "ri.affinity.unsupported": "Unsupported on this path",
  "ri.affinity.disabled": "Off",
  "ri.escalation.quality_gate_failed": "a quality gate failed",
  "ri.escalation.critic_rejected": "the critic rejected the work",
  "ri.escalation.repeated_test_failure": "tests failed repeatedly",
  "ri.escalation.no_progress": "no progress",
  "ri.escalation.context_capacity": "the context no longer fits",
  "ri.escalation.capability_missing": "a required capability is missing",
  "ri.escalation.provider_unavailable": "the provider is unavailable",
  "ri.escalation.quota_exhausted": "the quota is exhausted",
  "ri.escalation.high_risk_policy": "the high-risk policy",
  "ri.escalation.human_override": "a person's decision",
  "ri.domain.frontend": "Frontend",
  "ri.domain.backend": "Backend",
  "ri.domain.database": "Database",
  "ri.domain.devops": "DevOps",
  "ri.domain.security": "Security",
  "ri.domain.tests": "Tests",
  "ri.domain.documentation": "Documentation",
  "ri.domain.research": "Research",
  "ri.domain.integration": "Integration",
  "ri.domain.mixed": "Mixed",
  "ri.kind.investigation": "Investigation",
  "ri.kind.implementation": "Implementation",
  "ri.kind.refactor": "Refactor",
  "ri.kind.bugfix": "Bug fix",
  "ri.kind.test": "Test",
  "ri.kind.documentation": "Documentation",
  "ri.kind.review": "Review",
  "ri.kind.operation": "Operation",
  "ri.complexity.trivial": "Trivial",
  "ri.complexity.low": "Low",
  "ri.complexity.medium": "Medium",
  "ri.complexity.high": "High",
  "ri.complexity.critical": "Critical",

  // Routing decision (task detail)
  "ri.decision.title": "Routing decision",
  "ri.decision.loadFailed": "Could not load the routing decisions",
  "ri.decision.none": "No routing decision yet. One is recorded when an attempt of this task starts.",
  "ri.decision.classifier": "Classifier",
  "ri.decision.confidence": "Confidence",
  "ri.decision.class": "Class",
  "ri.decision.agent": "Agent",
  "ri.decision.agentVersion": "version {id}",
  "ri.decision.tier": "Tier",
  "ri.decision.requestedTier": "requested: {tier}",
  "ri.decision.model": "Model",
  "ri.decision.provider": "Provider",
  "ri.decision.cache": "Cache",
  "ri.decision.hit": "{pct} hit",
  "ri.decision.hitUnknown": "hit ratio unknown",
  "ri.decision.affinity": "Session affinity",
  "ri.decision.reason": "Reason",
  "ri.decision.premiumReview": "Premium review required",
  "ri.decision.escalated": "Escalated because {reason}",
  "ri.decision.rejected": "Rejected candidates ({n})",
  "ri.decision.noRejected": "No candidate was rejected.",
  "ri.decision.history": "Previous decisions",
  "ri.decision.decidedAt": "Decided {when}",

  // Model performance (Agents)
  "ri.perf.tab": "Model performance",
  "ri.perf.subtitle": "Which model delivers accepted work per task class, at what cost and with how much cache.",
  "ri.perf.groupBy": "Group by",
  "ri.perf.group.task_class,model,provider": "Task class × model × provider",
  "ri.perf.group.model,provider": "Model × provider",
  "ri.perf.group.task_class,tier": "Task class × tier",
  "ri.perf.group.agent_version,model": "Agent version × model",
  "ri.perf.group.tier": "Tier",
  "ri.perf.key.task_class": "Task class",
  "ri.perf.key.specialty": "Specialty",
  "ri.perf.key.risk": "Risk",
  "ri.perf.key.complexity": "Complexity",
  "ri.perf.key.agent_version": "Agent version",
  "ri.perf.key.model": "Model",
  "ri.perf.key.provider": "Provider",
  "ri.perf.key.reasoning_effort": "Reasoning effort",
  "ri.perf.key.tier": "Tier",
  "ri.perf.since": "Since {when}",
  "ri.perf.table": "Results per group",
  "ri.perf.col.tasks": "Tasks",
  "ri.perf.col.attempts": "Attempts",
  "ri.perf.col.accepted": "Accepted",
  "ri.perf.col.firstPass": "First pass",
  "ri.perf.col.costPerAccepted": "Cost per accepted task",
  "ri.perf.col.cacheHit": "Cache hit",
  "ri.perf.col.premiumEscalation": "Premium escalation",
  "ri.perf.col.medianDuration": "Median duration",
  "ri.perf.col.infra": "Infrastructure interruptions",
  "ri.perf.partialCost": "partial: cost unknown in {n} attempts",
  "ri.perf.infraHint": "Infrastructure interruptions (quota waits and provider outages) are not counted as failures.",
  "ri.perf.configExcluded": "{count} attempt(s) aborted by a model divergence (a route returning an unauthorized model) are a configuration error, not model quality, and are left out of this table.",
  "ri.perf.emptyTitle": "No routed attempts in this period",
  "ri.perf.emptyBody": "Results appear after attempts are routed by Routing Intelligence v2.",
  "ri.perf.maintainerOnly": "Only maintainers and admins can see the organization's model performance.",
  "ri.promo.title": "Economy vs premium: promotion",
  "ri.promo.hint": "A verdict is a hypothesis to calibrate, not a statistical guarantee.",
  "ri.promo.criteria": "Criteria: at least {min} evaluations, success gap up to {gap} pp, cost up to {cost}× and cycle time up to {cycle}× the premium model.",
  "ri.promo.none": "No economy model has been compared with a premium model yet.",
  "ri.promo.col.class": "Task class",
  "ri.promo.col.models": "Economy vs premium model",
  "ri.promo.vs": "vs {model}",
  "ri.promo.col.evaluations": "Evaluations (economy · premium)",
  "ri.promo.col.gap": "Success gap",
  "ri.promo.col.costRatio": "Cost ratio",
  "ri.promo.col.cycleRatio": "Cycle ratio",
  "ri.promo.col.verdict": "Verdict",
  "ri.promo.verdict.promote": "Promote",
  "ri.promo.verdict.hold": "Hold",
  "ri.promo.verdict.insufficient_data": "Insufficient data",
  "ri.promo.pp": "{n} pp",

  // Prompt cache (Observability)
  "ri.cache.title": "Prompt cache",
  "ri.cache.hint": "Across the organization, from the model calls of routed attempts.",
  "ri.cache.freshInput": "Fresh input tokens",
  "ri.cache.reads": "Cache reads",
  "ri.cache.writes": "Cache writes",
  "ri.cache.hitRatio": "Hit ratio",
  "ri.cache.savings": "Estimated savings",
  "ri.cache.savingsPartial": "partial: priced {known} of {total} attempts",
  "ri.cache.coldStarts": "Cold starts",
  "ri.cache.providerChanges": "Provider changes",
  "ri.cache.prefixChanges": "Prefix changes",
  "ri.cache.byState": "Attempts by cache state",
  "ri.cache.noAttempts": "No routed attempts in this period.",

  // Jev classifier (Observability)
  "ri.jev.title": "Jev classifier",
  "ri.jev.model": "Model",
  "ri.jev.policy": "Policy version",
  "ri.jev.routingV2": "Routing Intelligence v2",
  "ri.jev.jevOpenrouter": "Jev through OpenRouter",
  "ri.jev.on": "On",
  "ri.jev.off": "Off",
  "ri.jev.connection": "Connection",
  "ri.jev.noConnection": "No connection configured",
  "ri.jev.realCalls": "Real calls",
  "ri.jev.realAllowed": "Allowed on this server",
  "ri.jev.realNotAllowed": "Not allowed on this server",
  "ri.jev.readiness": "Readiness",
  "ri.jev.ready": "Ready",
  "ri.jev.notReady": "Not ready",
  "ri.jev.thresholds": "Thresholds",
  "ri.jev.thresholdsValue": "applies Jev from {auto} · combines with rules from {combine}",
  "ri.jev.impediments": "Impediments",
  "ri.jev.windowSince": "Since {when}",
  "ri.jev.countJev": "Jev classifications",
  "ri.jev.countRules": "Rules only",
  "ri.jev.countDegraded": "Degraded",
  "ri.jev.avgLatency": "Average latency",
  "ri.jev.cost": "Cost",
  "ri.jev.lastDegraded": "Last degraded",
  "ri.jev.neverDegraded": "Not in this period",
  "ri.jev.calibration": "Calibration: confidence × real outcome",
  "ri.jev.bin": "Confidence",
  "ri.jev.bin.lt_0_70": "below 70%",
  "ri.jev.bin.0_70_to_0_90": "70% to 90%",
  "ri.jev.bin.gte_0_90": "90% or more",
  "ri.jev.classifications": "Classifications",
  "ri.jev.accepted": "Accepted",
  "ri.jev.failed": "Failed",
  "ri.jev.noCalibration": "No classified attempt has finished yet.",
  "ri.jev.maintainerOnly": "Only maintainers and admins can see the Jev classifier.",

  // Jev paid test
  "ri.test.title": "Test classification",
  "ri.test.paidTitle": "This makes a real, paid call",
  "ri.test.paidBody": "The task below is sent to Jev through OpenRouter and the call is charged (usually a fraction of a cent). Set the most it may cost.",
  "ri.test.realCallsOff": "This server does not allow real calls for the connection; the test will be refused.",
  "ri.test.taskTitle": "Task title",
  "ri.test.description": "Description",
  "ri.test.type": "Type",
  "ri.test.risk": "Risk",
  "ri.test.maxCost": "Maximum cost (cents)",
  "ri.test.maxCostHint": "Required. More than 0 and up to 100 cents.",
  "ri.test.titleRequired": "Enter a task title.",
  "ri.test.maxCostInvalid": "Enter a cost above 0 and up to 100 cents.",
  "ri.test.submit": "Run paid test",
  "ri.test.notAllowedTitle": "Real calls are not enabled on this server",
  "ri.test.notAllowedBody": "The server refused the test before calling the provider. An operator must allow real calls for this connection first.",
  "ri.test.failed": "The classification failed",
  "ri.test.result": "Result",
  "ri.test.dimensions": "Dimensions",
  "ri.test.effective": "What MaestrOS would apply",
  "ri.test.source": "Source",
} as const;
